import React, {useEffect, useRef} from 'react';
import * as echarts from 'echarts';
import type {LevelNode} from '../../../../types/researchReport';

/**
 * 关键支撑阻力位横向条形图：从当前价延伸至该价位。
 * 暗色适配：色值硬编码自 tokens.css（ECharts 无法读取 CSS 变量）。
 */
const COLORS: Record<LevelNode['type'], string> = {
    resistance: '#ff5252',
    support: '#00d97e',
    current: '#f5a623',
};

export const LevelChart: React.FC<{ levels: LevelNode[] }> = ({levels}) => {
    const domRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<echarts.ECharts | null>(null);

    // 初始化一次，监听窗口缩放自适应
    useEffect(() => {
        if (!domRef.current) return;
        const chart = echarts.init(domRef.current);
        chartRef.current = chart;
        const onResize = () => chart.resize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            chart.dispose();
            chartRef.current = null;
        };
    }, []);

    // 数据变化时整体重设配置
    useEffect(() => {
        chartRef.current?.setOption(buildOption(levels), true);
    }, [levels]);

    return <div className="rr-chart-box" ref={domRef}/>;
};

/**
 * 构建 ECharts 配置：Y 轴为角色标签（阻力在上），X 轴为价格
 */
function buildOption(levels: LevelNode[]): echarts.EChartsOption {
    const current = levels.find(l => 'current' === l.type);
    const spot = current ? Number(current.price) : NaN;
    const valid = levels.filter(l => !Number.isNaN(Number(l.price)) && !Number.isNaN(spot));
    const labels = valid.map(l => l.role);
    // 区间柱用 stack 双系列：透明占位条 + 彩色长度条，柱子从 min(现价,价位) 延伸至 max(现价,价位)
    const base = valid.map(l => Math.min(spot, Number(l.price)));
    const bars = valid.map(l => ({
        value: Math.abs(Number(l.price) - spot),
        itemStyle: {color: COLORS[l.type]},
    }));
    return {
        backgroundColor: 'transparent',
        grid: {left: 110, right: 50, top: 20, bottom: 30},
        xAxis: {
            type: 'value',
            name: '价格($)',
            nameTextStyle: {color: '#3d5469'},
            scale: true,
            axisLine: {lineStyle: {color: '#1e2d3d'}},
            axisLabel: {color: '#7a93b0', fontSize: 10},
            splitLine: {lineStyle: {color: '#16202c'}},
        },
        yAxis: {
            type: 'category',
            data: labels,
            inverse: true,
            axisLine: {lineStyle: {color: '#1e2d3d'}},
            axisLabel: {color: '#7a93b0', fontSize: 10},
        },
        series: [
            {
                type: 'bar',
                stack: 'range',
                data: base,
                barWidth: 10,
                itemStyle: {opacity: 0},
                silent: true,
                tooltip: {show: false},
            },
            {
                type: 'bar',
                stack: 'range',
                data: bars,
                barWidth: 10,
                tooltip: {
                    trigger: 'item',
                    backgroundColor: 'rgba(13, 17, 23, 0.95)',
                    borderColor: '#2a3f55',
                    textStyle: {color: '#e2eaf4', fontSize: 11},
                    formatter: (params: any) => {
                        const item = valid[params.dataIndex];
                        return `${item.role}<br/>${item.description}<br/>价格: $${item.price}`;
                    },
                },
                label: {
                    show: true,
                    position: 'right',
                    color: '#7a93b0',
                    fontSize: 10,
                    formatter: (params: any) => `$${valid[params.dataIndex].price}`,
                },
            },
        ],
    };
}
